import React from 'react';
import compose from 'lodash/fp/compose';
import { useSelector } from 'react-redux';
import { useFavoriteOrganisation } from 'hooks/useFavoriteOrganisation';
import { useReferenceCheckingAccount } from 'hooks/useReferenceCheckingAccount';
import { useListCreditCard } from 'hooks/useListCreditCard';
import { 
  withTranslation, 
  withBSPosition,
  withUserPreferences,
  withPersonalInfo,
  withCurrency,
} from 'components/hoc';
import ViewDataContent from '../../common/ViewDataContent';
import LottieLoader from 'components/common/LottieLoader';
import { getFullName } from "components/utils";

const columns = [];

const LiabilityAccount = (props) => {
  const { bsPosition, fn, trObj, loaded, currencyLocaleOptions} = props
  const favoriteOrganisation = useFavoriteOrganisation();
  const referenceCheckingAccount = useReferenceCheckingAccount();
  const creditCardOptions = useListCreditCard();
  const persons = useSelector((state) => state.personalInfo.data);

  // Add filterableColumns configuration
  const filterableColumns = [
    {
      columnId: 'actual_value',
      columnDataType: 'number',
    },
    {
      columnId: 'financial_account',
      columnDataType: 'text',
    },
    {
      columnId: 'rec_op',
      columnDataType: 'text',
    },
    {
      columnId: 'actual_value_sc',
      columnDataType: 'number',
    },
    {
      columnId: 'person_id',
      columnDataType: 'text',
    },
    {
      columnId: 'position_link',
      columnDataType: 'text',
    },
    {
      columnId: 'account_category',
      columnDataType: 'text',
    },
    {
      columnId: 'currency',
      columnDataType: 'text',
    },
  ];


   const getPositionLinkName = (transactionItem) => {
    const { position_link, financial_account } = transactionItem;

    // Create lookup maps
    const personMap = new Map(
      persons.map((p) => [Number(p.id), `${p.first_name} ${p.last_name}`]),
    );
    const orgMap = new Map(
      favoriteOrganisation.map((o) => [String(o.id), o.name]),
    );
    const accountMap = new Map(
      referenceCheckingAccount.map((a) => [a.id, a.account_no]),
    );
    const creditCardMap = new Map(
      creditCardOptions.map((c) => [
        c.id,
        c.number,
        // c.issuing_org_detail?.name || c.number || c.id,
      ]),
    );

    if (['receive', 'payable'].includes(financial_account)) {
      return (
        personMap.get(Number(position_link)) ||
        orgMap.get(String(position_link)) ||
        position_link
      );
    }

    if (financial_account === 'bank') {
      return accountMap.get(position_link) || position_link;
    }

    if (financial_account === 'cash') {
      const matchedOption = currencyLocaleOptions.find(
        (currency) => currency.id === position_link
      );
      return matchedOption
        ? trObj(matchedOption)
        : position_link;
    }

    if (financial_account === 'credit') {
      return (
        creditCardMap.get(Number(position_link)) ||
        creditCardMap.get(String(position_link)) ||
        position_link
      );
    }

    return position_link;
  };


  const mapFields = () => {
    return bsPosition.filter(({ account_category }) => {
      return account_category === "liability";
    })
      .map((data) => {
        return {
          ...data,
          person: getFullName(data?.person_detail),
          actual_value: fn(data?.actual_value),
          actual_value_sc: fn(data?.actual_value_sc),
          position_link: getPositionLinkName(data),
          account_category: trObj(data.account_category_detail),
          financial_account: trObj(data.financial_account_detail),
        }
      })
  }

  const tableProps = {
    frontendTableName: "liability_account",
    backendTableName: "bs_position",
  }

  if (!loaded) {
    return (
      <>
          <LottieLoader />
      </>)
  }

  return (
    <ViewDataContent
      {...tableProps}
      columns={columns}
      data={mapFields() || []}
      filterableColumns={filterableColumns}
    />
  );
};


export default compose(
  withTranslation, 
  withBSPosition,
  withUserPreferences,
  withPersonalInfo,
  withCurrency,
)(LiabilityAccount);

